<template>
  <form-panel
    :id="'permanent-address'"
    :title="'Permanent Address'"
    :initially-expanded="!sameAsCurrentAddress"
    panel-content-class="grid grid-cols-1 gap-4 pb-6 mb-6 border-b border-gray-200 billing-form md:grid-cols-2 sm:gap-6"
  >
    <template v-if="!isStandAloneComponent" #panelHeaderCheckbox>
      <label class="flex text-sm md:col-span-2">
        <input
          v-model.lazy="value.sameAsCurrentAddress"
          type="checkbox"
          data-cy="checkSameCurrentAddress"
          :class="`${primaryColorClass}`"
          class="mt-1 form-checkbox"
        />
        <span class="ml-2">Same as current address</span>
      </label>
    </template>
    <template #content>
      <label class="block text-sm md:col-span-2">
        <span class="font-semibold text-gray-600 flex justify-between mb-2">
          <span>Address</span>
        </span>
        <input
          v-model="value.address"
          type="text"
          data-cy="permanentAddress"
          class="form-input block w-full min-h-10"
          :class="[
            inputIsDisabled,
            { 'bg-error-100': v$.value.address.$error },
          ]"
          :readonly="sameAsCurrentAddress"
        />
        <div
          v-if="v$.value.address.required.$invalid"
          class="error text-error-900"
          :class="{ hidden: !v$.value.address.$error }"
        >
          Field is required
        </div>
      </label>
      <label class="block text-sm">
        <span class="font-semibold text-gray-600 flex justify-between mb-2">
          <span>City</span>
        </span>
        <input
          v-model.lazy="value.city"
          data-cy="permanentAddressCity"
          type="text"
          class="form-input block w-full min-h-10"
          :class="[inputIsDisabled, { 'bg-error-100': v$.value.city.$error }]"
          :readonly="sameAsCurrentAddress"
        />
        <div
          v-if="v$.value.city.required.$invalid"
          class="error text-error-900"
          :class="{ hidden: !v$.value.city.$error }"
        >
          Field is required
        </div>
      </label>
      <label class="block text-sm">
        <span class="font-semibold text-gray-600 flex justify-between mb-2">
          <span>State/Province</span>
        </span>
        <input
          v-model.lazy="value.state"
          data-cy="permanentAddressState"
          type="text"
          class="form-input block w-full min-h-10"
          :class="[inputIsDisabled, { 'bg-error-100': v$.value.state.$error }]"
          :readonly="sameAsCurrentAddress"
        />
        <div
          v-if="v$.value.state.required.$invalid"
          class="error text-error-900"
          :class="{ hidden: !v$.value.state.$error }"
        >
          Field is required
        </div>
      </label>
      <label class="block text-sm">
        <span class="font-semibold text-gray-600 flex justify-between mb-2">
          <span>Country</span>
        </span>
        <VSelect
          v-model.lazy="value.country"
          data-cy="permanentAddressCountry"
          :value="value.country"
          :reduce="(country) => country.value"
          :options="countries"
          aria-label="Country"
          :class="[
            inputIsDisabled,
            { 'bg-error-100': v$.value.country.$error },
          ]"
          :disabled="sameAsCurrentAddress"
          @search="fetchCountries"
        ></VSelect>
        <div
          v-if="v$.value.country.required.$invalid"
          class="error text-error-900"
          :class="{ hidden: !v$.value.country.$error }"
        >
          Field is required
        </div>
      </label>
      <label class="block text-sm">
        <span class="font-semibold text-gray-600 flex justify-between mb-2">
          <span>Post Code</span>
        </span>
        <input
          v-model.lazy="value.postCode"
          data-cy="permanentAddressPostCode"
          type="text"
          class="form-input block w-full min-h-10"
          :class="[
            inputIsDisabled,
            { 'bg-error-100': v$.value.postCode.$error },
          ]"
          :readonly="sameAsCurrentAddress"
        />
        <div
          v-if="v$.value.postCode.required.$invalid"
          class="error text-error-900"
          :class="{ hidden: !v$.value.postCode.$error }"
        >
          Field is required
        </div>
      </label>
    </template>
  </form-panel>
</template>

<script>
import formPanel from "../SharedComponents/panel";
import countryListApi from "../../../mixins/countryListApi";

import { required } from "@vuelidate/validators";
import { eventBus } from "@/eventBus.js";
import useVuelidate from "@vuelidate/core";

export default {
  name: "PermanentAddress",
  components: { formPanel },
  mixins: [countryListApi],
  props: {
    permanentAddress: {
      type: Object,
      default: function () {
        return {};
      },
    },
    currentAddress: {
      type: Object,
      default: function () {
        return {};
      },
    },
    isStandAloneComponent: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:permanent-address"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      value: {
        address: "",
        city: "",
        state: "",
        country: "United States of America",
        postCode: "",
        sameAsCurrentAddress: false,
      },
    };
  },
  computed: {
    sameAsCurrentAddress() {
      return this.value.sameAsCurrentAddress;
    },
    inputIsDisabled() {
      return this.sameAsCurrentAddress
        ? "opacity-33 cursor-not-allowed border-2 bg-gray-200 text-gray-700"
        : "";
    },
  },
  watch: {
    value: {
      handler: function () {
        this.$emit("update:permanent-address", this.value);
      },
      deep: true,
      immediate: true,
    },
    currentAddress: function () {
      this.value.address = this.currentAddress.address;
      this.value.state = this.currentAddress.state;
      this.value.city = this.currentAddress.city;
      this.value.country = this.currentAddress.country;
      this.value.postCode = this.currentAddress.postCode;
    },
    sameAsCurrentAddress(newVal) {
      if (!this.isStandAloneComponent) {
        eventBus.$emit("passCurrentAddressToPermanentAddress", newVal);
      }
    },
  },
  validations: {
    value: {
      address: {
        required,
      },
      city: {
        required,
      },
      state: {
        required,
      },
      country: {
        required,
      },
      postCode: {
        required,
      },
    },
  },
};
</script>
